/**
 * BehaviorsTab — next-level toggle, track selector for IC4, all BehaviorCards,
 * behavior summary table, and overall behavior rating
 */
import { useState } from 'react';
import {
  C, FINN_VALUES, BEHAVIORS, RATING_SCALE, ALL_LEVELS,
  getBehaviorGroup, getRatingColor, getRatingLabel,
} from '../../data/managerReviewData';
import type { Employee, BehaviorData, Deliverable, FeedbackItem } from '../../types/managerReview';
import BehaviorCard from './BehaviorCard';

interface BehaviorsTabProps {
  employee: Employee;
  level: string;
  deliverables: Deliverable[];
  deliveryRating: number | null;
  behaviorsData: Record<string, BehaviorData>;
  setBehaviorsData: (fn: (prev: Record<string, BehaviorData>) => Record<string, BehaviorData>) => void;
  behaviorsRating: number | null;
  setBehaviorsRating: (r: number | null) => void;
  behaviorsRatingNote: string;
  setBehaviorsRatingNote: (n: string) => void;
  selfReview: string;
  feedbackItems: FeedbackItem[];
  perfFeedbackRaw?: string;
  anytimeFeedbackRaw?: string;
}

export default function BehaviorsTab({
  employee,
  level,
  deliverables,
  deliveryRating,
  behaviorsData,
  setBehaviorsData,
  behaviorsRating,
  setBehaviorsRating,
  behaviorsRatingNote,
  setBehaviorsRatingNote,
  selfReview,
  feedbackItems,
  perfFeedbackRaw,
  anytimeFeedbackRaw,
}: BehaviorsTabProps) {
  const [showNext, setShowNext] = useState(false);
  const [ic4Track, setIc4Track] = useState<'E' | 'M'>('E');

  const ratings = FINN_VALUES.map(v => behaviorsData[v.name]?.rating).filter(Boolean) as number[];
  const allRated = ratings.length === FINN_VALUES.length;
  const avg = allRated ? ratings.reduce((a, b) => a + b, 0) / ratings.length : null;
  const suggestedRating = avg !== null ? Math.round(avg) : null;
  const deviation = behaviorsRating && suggestedRating && behaviorsRating !== suggestedRating;

  const isIC4 = level === 'IC4';
  const levelIdx = ALL_LEVELS.indexOf(level as typeof ALL_LEVELS[number]);
  const nextLevel = isIC4
    ? (ic4Track === 'E' ? 'E5' : 'M5')
    : (levelIdx >= 0 && levelIdx < ALL_LEVELS.length - 1 ? ALL_LEVELS[levelIdx + 1] : null);
  const nextLevelLabel = nextLevel;
  const currentBehaviorGroup = getBehaviorGroup(level);
  const nextBehaviorGroup = nextLevel ? getBehaviorGroup(nextLevel) : null;
  const nextBehaviorData = showNext && nextBehaviorGroup && nextBehaviorGroup !== currentBehaviorGroup
    ? BEHAVIORS[nextBehaviorGroup] : null;

  return (
    <div>
      {/* Action row: next level toggle — above both columns so they align at the top */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8, marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {isIC4 && showNext && (['E', 'M'] as const).map(t => (
          <button key={t} onClick={() => setIc4Track(t)} style={{
            padding: '4px 12px', borderRadius: 20, fontSize: 11, cursor: 'pointer', fontFamily: 'Inter',
            border: `1.5px solid ${ic4Track === t ? C.blue : '#ddd'}`,
            background: ic4Track === t ? C.lightBlue : '#fff',
            color: ic4Track === t ? C.blue : '#999', fontWeight: ic4Track === t ? 700 : 400,
          }}>
            {t === 'E' ? 'E5 Expert' : 'M5 Manager'}
          </button>
        ))}
        {nextLevel && (
          <button onClick={() => setShowNext(n => !n)} style={{
            padding: '6px 14px', borderRadius: 8, fontSize: 12, fontWeight: 600,
            cursor: 'pointer', fontFamily: 'Inter', transition: 'all 0.15s',
            border: `1.5px solid ${showNext ? C.blue : '#ddd'}`,
            background: showNext ? C.lightBlue : '#fff',
            color: showNext ? C.blue : '#777',
          }}>{showNext ? '✕ Hide' : '＋ Show'} {nextLevelLabel}</button>
        )}
        </div>
      </div>

      <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start' }}>
      {/* ── Left column ── */}
      <div style={{ flex: 1, minWidth: 0 }}>
      {FINN_VALUES.map((v, i) => (
        <BehaviorCard
          key={v.name}
          index={i}
          id={`behavior-${v.name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')}`}
          value={v}
          level={level}
          data={behaviorsData[v.name] || { rating: null, observed: {}, strengthsText: '', improvementsText: '' }}
          onChange={d => setBehaviorsData(prev => ({ ...prev, [v.name]: d }))}
          aiContext={{
            employeeName: employee.name,
            role: employee.role,
            level,
            valueName: v.name,
            selfReview,
            feedbackItems,
            perfFeedbackRaw,
            anytimeFeedbackRaw,
            field: 'behaviors',
            deliveryRating,
            deliverables: deliverables
              .filter(d => d.title || d.rating)
              .map(d => ({ title: d.title, rating: d.rating, competencies: d.competencies })),
          }}
          nextBullets={nextBehaviorData ? (nextBehaviorData[v.name] || []) : null}
          nextLevelLabel={nextLevelLabel}
          deliverables={deliverables}
        />
      ))}

      </div>{/* end left column */}

      {/* ── Right column: sticky Overall Behavior Rating ── */}
      <div id="behavior-summary" style={{ width: 380, flexShrink: 0, position: 'sticky', top: 96, alignSelf: 'flex-start' }}>
        <div style={{
          background: '#fff', borderRadius: 10, border: `1px solid ${C.border}`,
          boxShadow: '0 2px 12px rgba(0,0,0,0.06)', overflow: 'hidden',
        }}>
          <div style={{ padding: '12px 16px', borderBottom: '1px solid #f0f0f0', background: '#fafafa' }}>
            <div style={{ fontWeight: 700, fontSize: 13, color: '#0f0f0f' }}>Behavior Summary</div>
          </div>
          <div style={{ padding: '12px 16px' }}>

            {/* Nudge — appears once all values are rated but overall not yet set */}
            {allRated && !behaviorsRating && (
              <div style={{
                marginBottom: 12, padding: '9px 12px',
                background: '#f0f7ff', border: '1.5px solid #0087eb', borderRadius: 7,
                fontSize: 12, fontWeight: 600, color: '#0087eb',
                display: 'flex', alignItems: 'flex-start', gap: 7, lineHeight: 1.4,
              }}>
                <span>↓</span>
                <span>All values rated — now set your overall behavior rating.</span>
              </div>
            )}

            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <tbody>
                {FINN_VALUES.map((v, vi) => {
                  const r = behaviorsData[v.name]?.rating;
                  return (
                    <tr key={v.name} style={{ borderBottom: '1px solid #f5f5f5' }}>
                      <td style={{ padding: '7px 0', paddingRight: 8 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <div style={{
                            width: 18, height: 18, borderRadius: 5, background: '#ebebeb', color: '#999',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: 9, fontWeight: 800, flexShrink: 0,
                          }}>{vi + 1}</div>
                          <span style={{ fontSize: 12, color: '#666', fontWeight: 500 }}>{v.name}</span>
                        </div>
                      </td>
                      <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                        {r ? (
                          <span style={{
                            display: 'inline-block', padding: '2px 8px', borderRadius: 5,
                            background: getRatingColor(r) + '18', border: `1.5px solid ${getRatingColor(r)}`,
                            color: getRatingColor(r), fontSize: 11, fontWeight: 700,
                          }}>
                            {r} – {getRatingLabel(r)}
                          </span>
                        ) : <span style={{ fontSize: 11, color: '#ccc' }}>—</span>}
                      </td>
                    </tr>
                  );
                })}

                {/* Suggested row — no border, lighter background to distinguish from individual ratings */}
                {allRated && avg !== null && suggestedRating && (() => {
                  const sr = RATING_SCALE.find(r => r.v === suggestedRating);
                  const col = getRatingColor(suggestedRating);
                  return (
                    <tr>
                      <td style={{ padding: '8px 0 2px', fontSize: 12, color: '#888', fontWeight: 500, paddingRight: 8 }}>
                        Suggested <span style={{ color: '#bbb', fontWeight: 400 }}>({avg.toFixed(1)} avg)</span>
                      </td>
                      <td style={{ textAlign: 'right', whiteSpace: 'nowrap', paddingTop: 6, paddingBottom: 2 }}>
                        <span style={{
                          display: 'inline-block', padding: '2px 8px', borderRadius: 5,
                          background: col + '1a', color: col, fontSize: 11, fontWeight: 700,
                        }}>
                          {suggestedRating} – {sr?.label}
                        </span>
                      </td>
                    </tr>
                  );
                })()}
              </tbody>
            </table>

            {/* Overall rating card — three states: locked / active / done */}
            {(() => {
              const isLocked = !allRated;
              const isDone = !!behaviorsRating;
              const cardBorder = isLocked ? '#e0e0e0' : isDone ? '#26bd59' : '#0087eb';
              const cardBg = isLocked ? '#f8f8f8' : isDone ? '#f0faf4' : '#f0f7ff';
              const badgeBg = isLocked ? '#ccc' : isDone ? '#26bd59' : '#0087eb';
              const badgeText = isLocked ? 'Rate all 5 values first' : isDone ? '✓ Done' : 'Required · Your judgment call';
              return (
                <div style={{ marginTop: 14, padding: '12px 14px', background: cardBg, borderRadius: 8, border: `1.5px solid ${cardBorder}` }}>
                  <div style={{
                    display: 'inline-flex', alignItems: 'center',
                    background: badgeBg, color: '#fff',
                    fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 4,
                    letterSpacing: '0.04em', marginBottom: 8,
                  }}>{badgeText}</div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: isLocked ? '#aaa' : '#1a1a1a', marginBottom: 4 }}>
                    Overall Behavior Rating
                  </div>
                  <div style={{ fontSize: 11, color: isLocked ? '#bbb' : '#666', lineHeight: 1.5, marginBottom: isLocked ? 0 : 10 }}>
                    All values carry equal weight — the average is your starting point.
                  </div>
                  {!isLocked && (
                    <>
                      <select
                        value={behaviorsRating || ''}
                        onChange={e => setBehaviorsRating(e.target.value ? Number(e.target.value) : null)}
                        style={{
                          width: '100%', padding: '7px 10px', borderRadius: 7,
                          border: `1.5px solid ${behaviorsRating ? getRatingColor(behaviorsRating) : C.border}`,
                          fontSize: 12, fontFamily: 'Inter', fontWeight: 600,
                          color: behaviorsRating ? getRatingColor(behaviorsRating) : C.muted,
                          background: behaviorsRating ? getRatingColor(behaviorsRating) + '18' : '#fff',
                          outline: 'none', cursor: 'pointer',
                        }}
                      >
                        <option value=''>Select…</option>
                        {RATING_SCALE.map(r => <option key={r.v} value={r.v}>{r.v} – {r.label}</option>)}
                      </select>
                      <div style={{ marginTop: 8, fontSize: 11, color: '#888', display: 'flex', alignItems: 'flex-start', gap: 5, lineHeight: 1.5 }}>
                        <span style={{ flexShrink: 0 }}>ℹ</span>
                        <span>This rating will be shared with the employee in their review conversation.</span>
                      </div>
                    </>
                  )}
                </div>
              );
            })()}

            {/* Deviation note */}
            {deviation && (
              <div style={{
                marginTop: 12, padding: '10px 12px', background: '#f8f8f8',
                borderRadius: 8, borderLeft: '3px solid #d1d5db',
              }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#444', marginBottom: 4 }}>
                  Rating differs from suggestion — add a note.
                </div>
                <textarea
                  value={behaviorsRatingNote}
                  onChange={e => setBehaviorsRatingNote(e.target.value)}
                  placeholder="e.g. Aim High and Own It were both clear 4s, but Embrace Openness pulled the average down…"
                  rows={3}
                  style={{
                    width: '100%', borderRadius: 6, border: '1.5px solid #d1d5db',
                    padding: '8px 10px', fontSize: 11, fontFamily: 'Inter',
                    resize: 'vertical', boxSizing: 'border-box', lineHeight: 1.55,
                    outline: 'none', background: '#fff', marginTop: 4,
                  }}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      </div>{/* end two-column flex */}
    </div>
  );
}
